import { useState, type FC } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Label, TextInput, Checkbox, Spinner } from 'flowbite-react';

interface SaveFilterModalProps {
  show: boolean;
  onClose: () => void;
  onSave: (name: string, description: string, isDefault: boolean) => Promise<void>;
}

export const SaveFilterModal: FC<SaveFilterModalProps> = ({
  show,
  onClose,
  onSave,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Bitte geben Sie einen Namen für die Ansicht ein.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onSave(name.trim(), description.trim(), isDefault);
      setName('');
      setDescription('');
      setIsDefault(false);
      onClose();
    } catch (err: any) {
      console.error('Error saving preset:', err);
      setError(err?.message || 'Fehler beim Speichern der Vorlage');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onClose={onClose} size="md">
      <ModalHeader>Ansicht als Vorlage speichern</ModalHeader>
      <form onSubmit={handleSubmit}>
        <ModalBody className="space-y-4">
          {error && (
            <div className="rounded-lg bg-red-50 p-3 text-xs text-red-700 dark:bg-red-900/30 dark:text-red-300">
              {error}
            </div>
          )}

          <div>
            <div className="mb-2 block">
              <Label htmlFor="filter-preset-name">Name der Vorlage *</Label>
            </div>
            <TextInput
              id="filter-preset-name"
              placeholder="z. B. Berlin Team Strom 2026"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
          </div>

          <div>
            <div className="mb-2 block">
              <Label htmlFor="filter-preset-desc">Beschreibung (optional)</Label>
            </div>
            <TextInput
              id="filter-preset-desc"
              placeholder="z. B. Fokus auf Neukundenakquise"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-2 pt-2">
            <Checkbox
              id="filter-preset-default"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
            />
            <Label htmlFor="filter-preset-default" className="text-xs font-normal">
              Als meine Standardansicht für die Heatmap festlegen
            </Label>
          </div>
        </ModalBody>
        <ModalFooter className="flex justify-end gap-2">
          <Button color="gray" size="sm" onClick={onClose} disabled={isSubmitting}>
            Abbrechen
          </Button>
          <Button type="submit" color="blue" size="sm" disabled={isSubmitting}>
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <Spinner size="xs" /> Speichern...
              </span>
            ) : (
              'Vorlage speichern'
            )}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
